import { useState } from 'react';
import { ConfirmationDialog } from '@/shared/components/confirmation-dialog';
import { Icon, IconKey } from '@/shared/components/icon';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { formatRelativeDate } from '@/shared/utils/format';
import { useConversations, useDeleteConversation } from '../hooks';
import { useAIChat } from '../context';

export function ConversationListPanel() {
  const { data, isLoading } = useConversations();
  const { activeConversationId, openChat, startNewConversation } = useAIChat();
  const deleteMutation = useDeleteConversation();
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className='flex-1 p-4 space-y-4'>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className='space-y-2'>
            <Skeleton className='h-4 w-3/4' />
            <Skeleton className='h-3 w-1/2' />
          </div>
        ))}
      </div>
    );
  }

  const conversations = data?.items ?? [];

  if (conversations.length === 0) {
    return (
      <div className='flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center'>
        <Icon icon={IconKey.Sparkles} size={48} className='text-text/20' />
        <h3 className='text-sm font-semibold'>No conversations yet</h3>
        <p className='text-xs text-text/50'>
          Ask Tally anything about your household finances.
        </p>
        <Button size='sm' onClick={startNewConversation} className='mt-2'>
          Start a conversation
        </Button>
      </div>
    );
  }

  return (
    <div className='flex-1 overflow-y-auto'>
      {conversations.map((conversation) => (
        <div key={conversation.id} className='group relative'>
          <button
            onClick={() => openChat(conversation.id)}
            className='flex flex-col gap-0.5 px-4 pr-10 py-3 w-full text-left hover:bg-secondary/30 transition-colors border-b border-border/20 hover:cursor-pointer'
          >
            <span className='text-sm font-medium truncate'>
              {conversation.title ?? 'New conversation'}
            </span>
            <span className='text-xs text-text/50'>
              {conversation.message_count} {conversation.message_count === 1 ? 'message' : 'messages'}
              {' · '}
              {formatRelativeDate(conversation.updated_at)}
            </span>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteTarget(conversation.id);
            }}
            className='absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md opacity-0 group-hover:opacity-100 hover:bg-destructive/10 text-text/40 hover:text-destructive transition-all'
            aria-label='Delete conversation'
          >
            <Icon icon={IconKey.Trash} size={14} />
          </button>
        </div>
      ))}

      <ConfirmationDialog
        isOpen={deleteTarget !== null}
        title='Delete conversation'
        description='This conversation and all its messages will be permanently removed.'
        confirmLabel='Delete'
        onConfirm={() => {
          if (deleteTarget) {
            const isActive = activeConversationId === deleteTarget;
            deleteMutation.mutate(deleteTarget, {
              onSuccess: () => {
                if (isActive) {
                  openChat();
                }
              },
            });
            setDeleteTarget(null);
          }
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
